import { Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent, Chip, Grid, Icon, Stack } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getBgColor, getIcon, getTitle } from 'pgsse/util/PgsseUtil'
import { NewTable } from 'components/datatable/NewTable'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import { getI18nTitleDataLength, getSandreLabel } from 'utils/StringUtil'
import ActionDialog from './ActionDialog'
import SimpleTabList from 'components/list/SimpleTabList'
import { lightGrey, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import DtoActionTable from 'pgsse/dto/DtoActionTable'
import { compact, isEmpty, isNil, keyBy, map } from 'lodash'
import DtoDangerTable from 'pgsse/dto/DtoDangerTable'
import { getYear } from 'utils/DateUtil'
import moment from 'moment'
import { exportFile } from 'utils/ExportDataUtil'
import { getTruncate } from 'utils/StoreUtils'

const STATISTICS = 1
const ACTIONS = 2

const EXPORT_HEADERS = ['structure', 'dangerGroup', 'localisation', 'equipmentType', 'category',
    'responsible', 'deadline', 'interventionType', 'comment', 'correctionDate', 'commentCorrection', 'isFixed']

const CardAction = ({
    action,
    danger,
    setSelectedDanger,
    setIsDialogOpen,
    completedYear,
}) => {
    const {
        sandreCodes,
        installations,
        installationsTypes,
        contributors,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        installations: store.InstallationReducer.installationsIndex,
        contributors: store.ContributorReducer.contributorsIndex,
        installationsTypes: store.InstallationReducer.installationsTypes,
    }), shallowEqual)

    const structureName = useSandreList(SANDRE.PGSSE_STRUCTURE).find(s => s.code === action.idStructure)?.name

    return (
        <Card
            sx={{ cursor: 'pointer' }}
            onClick={() => {
                setIsDialogOpen(true)
                setSelectedDanger(danger)
            }}
        >
            <Grid
                container
                size={12}
                sx={{
                    padding: '5 10',
                    backgroundColor: lightGrey,
                    fontWeight: 'bold',
                    justifyContent: 'space-between',
                }}
            >
                <Grid container alignItems='center' columnSpacing={1}>
                    {getIcon(danger, contributors, installations)}
                    {getTitle(danger, contributors, installations, installationsTypes)}
                </Grid>
                {structureName}
                <Grid>
                    <Chip
                        label={<>{i18n.risk} : {action.riskValue}</>}
                        sx={{
                            color: 'white',
                            backgroundColor: getBgColor((danger.severity * danger.frequency) / danger.detectability),
                            height: '1.5rem',
                        }}
                    />
                </Grid>
            </Grid>
            <Grid container size={12} spacing={1} sx={{ padding: 1 }}>
                <Grid size={3}>
                    <strong>{i18n.risk} : </strong>
                    {getSandreLabel(sandreCodes, SANDRE.VISITES_ACTIONS_RISQUE, action.risk)}
                </Grid>
                <Grid size={3}>
                    <strong>{i18n.localisation} : </strong>
                    {getSandreLabel(sandreCodes, SANDRE.VISITES_ACTIONS_SECTEUR, action.localisation)}
                </Grid>
                <Grid size={3}>
                    <strong>{i18n.equipmentType} : </strong>
                    {getSandreLabel(sandreCodes, SANDRE.EQUIPEMENTS_TYPE_ASST, action.equipmentType)}
                </Grid>
                <Grid size={3}>
                    <strong>{i18n.responsible} : </strong>
                    {getSandreLabel(sandreCodes, SANDRE.VISITES_ACTIONS_RESPONSABILITE, action.responsible)}
                </Grid>
                <Grid size={3}>
                    <strong>{i18n.category} : </strong>
                    {getSandreLabel(sandreCodes, SANDRE.VISITES_ACTIONS_CATEGORIE, action.category)}
                </Grid>
                <Grid size={3}>
                    <strong>{i18n.interventionType} : </strong>
                    {getSandreLabel(sandreCodes, SANDRE.VISITES_ACTIONS_TYPE_INTERVENTION, action.interventionType)}
                </Grid>
                <Grid size={6}>
                    <strong>{i18n.comment} : </strong>
                    {action.comment}
                </Grid>
                {completedYear && <Grid size={12}>{i18n.actionCompletedIn} {completedYear}</Grid>}
            </Grid>
        </Card>
    )
}

CardAction.propTypes = {
    action: PropTypes.instanceOf(DtoActionTable),
    danger: PropTypes.instanceOf(DtoDangerTable),
    setSelectedDanger: PropTypes.func,
    setIsDialogOpen: PropTypes.func,
    completedYear: PropTypes.number,
}

const AccordionDangerTable = ({
    dangers = [],
    actions = [],
    setIsOpen,
    setSelectedDanger,
}) => {
    const {
        sandreCodes,
        installations,
        contributors,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        installations: store.InstallationReducer.installationsIndex,
        contributors: store.ContributorReducer.contributorsIndex,
    }), shallowEqual)

    const handleEditElement = ({ idDanger }) => {
        setIsOpen(true)
        setSelectedDanger(dangers.find(d => d.idDanger === idDanger))
    }

    const headers = [{ key: 'nullValue', value: '' }, 'nbActions', 'family', 'type', 'group', 'precision', 'frequency', 'initialRisk', 'residualRisk', 'measureTaken']

    const actionsByDanger = useMemo(() => (
        actions.reduce((acc, action) => {
            acc[action.idDanger] = (acc[action.idDanger] || 0) + 1
            return acc
        }, {})
    ), [actions])

    const formattedDangers = useMemo(() => dangers.map(danger => {
        const cotation = (danger.frequency * danger.severity) || undefined
        const residualRisk = (cotation / danger.detectability) || undefined

        const nbActions = actionsByDanger[danger.idDanger] || 0

        return {
            ...danger,
            nbActions,
            family: getSandreLabel(sandreCodes, `${SANDRE.PGSSE_FAMILLE}.${danger.idStructure}`, danger.familyType),
            type: getSandreLabel(sandreCodes, SANDRE.PGSSE_TYPE_DANGER, danger.dangerType),
            group: getSandreLabel(sandreCodes, SANDRE.PGSSE_GROUP_DANGER, danger.dangerGroup),
            frequency: getSandreLabel(sandreCodes, SANDRE.PGSSE_FREQUENCE, danger.frequency),
            severity: getSandreLabel(sandreCodes, SANDRE.PGSSE_GRAVITE, danger.severity),
            residualRisk: {
                value: residualRisk && <Box sx={{ bgcolor: getBgColor(residualRisk), color: 'white', paddingLeft: '0.5rem' }}> {residualRisk} </Box>,
                sortValue: residualRisk,
            },
            initialRisk: cotation,
            measureTaken: { value: danger.onSiteMeasure && <Icon>check_value</Icon>, sortValue: danger.onSiteMeasure },
            colorLine: nbActions === 0 && residualRisk > 50 && PASTEL_COLOR.orange,
            nullValue: { value: getIcon(danger, contributors, installations) },
            precision: getTruncate(danger.precision, 30),
        }
    }), [dangers, actionsByDanger, sandreCodes, contributors, installations])

    return (
        <NewTable
            rows={formattedDangers}
            headers={headers}
            onClickRow={handleEditElement}
            WrapperComponent={AccordionDetails}
        />
    )
}

AccordionDangerTable.propTypes = {
    dangers: PropTypes.arrayOf(PropTypes.instanceOf(DtoDangerTable)),
    actions: PropTypes.arrayOf(PropTypes.instanceOf(DtoActionTable)),
    setIsOpen: PropTypes.func,
    setSelectedDanger: PropTypes.func,
}

const ActionTable = ({
    readMode = false,
    stateAUD = {},
    dispatchStateAUD,
}) => {
    const {
        dangerElements,
        actionElements,
        installationsIndex,
        sandreCodes,
    } = useSelector(store => ({
        dangerElements: store.PgsseReducer.dangers,
        actionElements: store.PgsseReducer.actions,
        installationsIndex: store.InstallationReducer.installationsIndex,
        sandreCodes: store.ReferencialReducer.sandreCodes,
    }), shallowEqual)

    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [selectedDanger, setSelectedDanger] = useState(false)

    const structures = useSandreList(SANDRE.PGSSE_STRUCTURE)

    const dangerById = useMemo(() => keyBy(dangerElements, 'idDanger'), [dangerElements])

    const stateActionsList = useMemo(() => {
        const actionsWithoutDeleted = actionElements.filter(d => !stateAUD.deletedActions.includes(d.idAction))
            .map(d => stateAUD.updatedActions.find(ud => ud.idAction === d.idAction) || d)
        return [
            ...actionsWithoutDeleted,
            ...stateAUD.addedActions,
        ].map((d, index) => ({ ...d, index }))
    }, [actionElements, stateAUD])

    const actionsWithDanger = useMemo(() => stateActionsList.map(action => {
        const danger = dangerById[action.idDanger] || {}
        return {
            ...action,
            familyType: danger.familyType,
            idStructure: danger.idStructure,
            riskValue: !isNil(danger.severity) && !isNil(danger.frequency) && !isNil(danger.detectability)
                ? danger.severity * danger.frequency / danger.detectability
                : null,
        }
    }), [stateActionsList, dangerById])

    const actionsByYear = useMemo(() => {
        return actionsWithDanger.reduce((acc, action) => {
            const creationYear = moment(action.creationDate).year()
            const correctionYear = action.correctionDate ? moment(action.correctionDate).year() : null
            return {
                ...acc,
                [creationYear]: {
                    ...(acc[creationYear] ?? { created: [], completed: [] }),
                    created: [
                        ...(acc[creationYear]?.created ?? []),
                        action,
                    ],
                },
                ...(correctionYear && {
                    [correctionYear]: {
                        ...(acc[correctionYear] ?? { created: [], completed: [] }),
                        completed: [
                            ...(acc[correctionYear]?.completed ?? []),
                            action,
                        ],
                    },
                }),
            }
        }, {})
    }, [actionsWithDanger])

    const orderedAnnualActions = useMemo(() =>
        map(actionsByYear, (data, year) => ({ year, ...data }))
            .sort((a, b) => b.year - a.year), [actionsByYear])

    const getFormattedExport = () =>
        actionElements.map((action, i) => {
            const danger = dangerById[action.idDanger] || {}
            const structure = structures.find(s => s.code === danger.idStructure)?.name || ''
            return ({
                ...action,
                structure,
                dangerGroup: getSandreLabel(sandreCodes, SANDRE.VISITES_ACTIONS_RISQUE, action.dangerGroup),
                localisation: getSandreLabel(sandreCodes, SANDRE.VISITES_ACTIONS_SECTEUR, action.localisation),
                equipmentType: getSandreLabel(sandreCodes, SANDRE.EQUIPEMENTS_TYPE_ASST, action.equipmentType),
                category: getSandreLabel(sandreCodes, SANDRE.VISITES_ACTIONS_CATEGORIE, action.category),
                responsible: getSandreLabel(sandreCodes, SANDRE.VISITES_ACTIONS_RESPONSABILITE, action.responsible),
                deadline: getSandreLabel(sandreCodes, SANDRE.VISITES_ACTIONS_ECHEANCE, action.deadline),
                interventionType: getSandreLabel(sandreCodes, SANDRE.VISITES_ACTIONS_TYPE_INTERVENTION, action.interventionType),
                isFixed: action.correctionDate <= moment.now(),
                headers: i === 0 ? EXPORT_HEADERS : [],
            })
        })

    const actions = compact([
        !isEmpty(installationsIndex) && {
            icon: 'file_download',
            tooltip: i18n.export,
            color: 'black',
            onClick: () =>
                exportFile({
                    data: getFormattedExport(),
                    exportType: 'xlsx',
                    titleFile: i18n.actions,
                }),
        },
    ])

    return (
        <>
            <SimpleTabList
                tabs={[
                    {
                        constant: STATISTICS,
                        label: i18n.statistics,
                        icon: 'bar_chart',
                    },
                    {
                        constant: ACTIONS,
                        label: i18n.actions,
                        icon: 'build',
                    },
                ]}
                noPadding
                defaultTab={STATISTICS}
            >
                {tab => (
                    <>
                        {tab === STATISTICS && (
                            <Card>
                                <CardTitle title={i18n.listOfActionsByHazard} actions={actions} />
                                <CardContent>
                                    <Grid
                                        container
                                        spacing={1}
                                        direction={'column'}
                                    >
                                        {structures.map((s, i) => {
                                            const filteredDangers = dangerElements.filter(({ idStructure }) => idStructure === s.code)
                                            return (
                                                <Accordion
                                                    key={s.name}
                                                    defaultExpanded={i===0}
                                                >
                                                    <AccordionSummary>{`${s.name} (${filteredDangers.length} ${getI18nTitleDataLength(i18n.element, i18n.elements, filteredDangers.length)})`}</AccordionSummary>
                                                    <AccordionDangerTable
                                                        dangers={filteredDangers}
                                                        actions={stateActionsList}
                                                        setIsOpen={setIsDialogOpen}
                                                        setSelectedDanger={setSelectedDanger}
                                                    />
                                                </Accordion>
                                            )
                                        })}
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}
                        {tab === ACTIONS && (
                            <Card sx={{ padding: 1 }}>
                                <Stack spacing={1}>
                                    {orderedAnnualActions.length === 0 ? i18n.noActionForThisDanger : (
                                        orderedAnnualActions.map((annualActions, index) => {
                                            const createdActions = annualActions.created.filter(action => {
                                                const correctionYear = action.correctionDate && getYear(action.correctionDate)
                                                return correctionYear !== parseInt(annualActions.year)
                                            })
                                            const completedActions = annualActions.completed

                                            return (
                                                <Accordion defaultExpanded={index === 0} key={annualActions.year}>
                                                    <AccordionSummary>{annualActions.year}</AccordionSummary>
                                                    <AccordionDetails>
                                                        <Stack spacing={1}>
                                                            <Accordion defaultExpanded>
                                                                <AccordionSummary>{i18n.createdActions} ({createdActions.length})</AccordionSummary>
                                                                <AccordionDetails>
                                                                    <Stack spacing={1}>
                                                                        {createdActions
                                                                            .map(action => (
                                                                                <CardAction
                                                                                    key={action.idAction}
                                                                                    action={action}
                                                                                    danger={dangerById[action.idDanger] || {}}
                                                                                    setIsDialogOpen={setIsDialogOpen}
                                                                                    setSelectedDanger={setSelectedDanger}
                                                                                    completedYear={action.correctionDate && getYear(action.correctionDate)}
                                                                                />
                                                                            ))}
                                                                    </Stack>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                            <Accordion>
                                                                <AccordionSummary>{i18n.completedActions} ({completedActions.length})</AccordionSummary>
                                                                <AccordionDetails>
                                                                    <Stack spacing={1}>
                                                                        {completedActions.map(action => (
                                                                            <CardAction
                                                                                key={action.idAction}
                                                                                action={action}
                                                                                danger={dangerElements.find(d => d.idDanger === action.idDanger) || {}}
                                                                                setIsDialogOpen={setIsDialogOpen}
                                                                                setSelectedDanger={setSelectedDanger}
                                                                            />
                                                                        ))}
                                                                    </Stack>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        </Stack>
                                                    </AccordionDetails>
                                                </Accordion>
                                            )
                                        })
                                    )}
                                </Stack>
                            </Card>
                        )}
                    </>
                )}
            </SimpleTabList>
            <ActionDialog
                danger={selectedDanger}
                isOpen={isDialogOpen}
                setIsOpen={setIsDialogOpen}
                dispatchStateAUD={dispatchStateAUD}
                stateAUD={stateAUD}
                readMode={readMode}
            />
        </>
    )
}

ActionTable.propTypes = {
    readMode: PropTypes.bool,
    stateAUD: PropTypes.shape({}),
    dispatchStateAUD: PropTypes.func,
}

export default ActionTable
